import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { TID } from "@/lib/testIds";
import { ArrowUpRight, Bot, LineChart, ShieldCheck, Zap, TrendingUp, TrendingDown, AlertTriangle, ChevronDown } from "lucide-react";
import { RiskBanner, RiskFooter, RiskDisclaimerModal } from "@/components/RiskDisclaimer";
import Pricing from "@/components/Pricing";
import { FounderChip } from "@/components/FounderWelcome";
import LiveDailyChip from "@/components/LiveDailyChip";
import TradingChartBackground from "@/components/TradingChartBackground";
import LandingLeaderboard from "@/components/LandingLeaderboard";
import AuthModal from "@/components/AuthModal";
import Chatbot from "@/components/Chatbot";
import PromoVideoPopup from "@/components/PromoVideoPopup";
import ShareBar from "@/components/ShareBar";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  // Capture ?ref=CODE for referral attribution
  try {
    const p = new URLSearchParams(window.location.search);
    const ref = p.get("ref");
    if (ref) window.localStorage.setItem("tradingox_ref_code", ref.trim().toUpperCase());
  } catch (err) { console.debug("[Landing] ref capture failed", err); }
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Landing() {
  const navigate = useNavigate();
  const [showRisk, setShowRisk] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const openAuth = (mode) => {
    if (mode === "register") {
      // Signup now flows through the gated disclaimer → form page
      navigate("/signup");
      return;
    }
    setAuthMode(mode);
    setAuthOpen(true);
  };
  return (
    <div className="min-h-screen text-slate-100 relative overflow-x-hidden bg-[#070810]">
      <TradingChartBackground className="absolute inset-x-0 top-0 h-[900px] w-full opacity-100 z-0" />
      <div className="absolute inset-x-0 top-0 h-[900px] bg-gradient-to-b from-transparent via-[#070810]/40 to-[#070810] pointer-events-none z-0" />
      <div className="absolute inset-0 aq-hero-glow pointer-events-none z-0" />
      <RiskBanner onOpen={() => setShowRisk(true)} testid="risk-banner-landing" />
      <RiskDisclaimerModal open={showRisk} onOpenChange={setShowRisk} />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode={authMode} />
      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-4 sm:px-6 lg:px-12 py-4 sm:py-5 border-b border-white/5 backdrop-blur-md bg-[#090A0F]/60">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img src="/brand/tradingox-logo.png" alt="TradingoX" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)] shrink-0" />
          <div className="font-display text-base sm:text-lg font-bold tracking-tight truncate">TradingoX</div>
          <span className="hidden sm:inline-block ml-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">v1.1 // scalping</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-menu-trigger"
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-slate-300 hover:text-white transition rounded-full border border-white/10 hover:border-white/25 bg-white/5 hover:bg-white/10 px-3 sm:px-3.5 py-1.5"
              >
                Menu <ChevronDown className="w-3.5 h-3.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="w-56 bg-[#0F111A]/95 backdrop-blur-md border border-white/10 text-slate-200"
            >
              <DropdownMenuItem asChild>
                <a href="/#how" data-testid="nav-how-link" className="cursor-pointer text-sm">
                  How It Works
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/pricing" data-testid="nav-pricing-link" className="cursor-pointer text-sm">
                  Pricing
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/leaderboard" data-testid="nav-leaderboard-link" className="cursor-pointer text-sm">
                  Leaderboard
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/performance" data-testid="nav-performance-link" className="cursor-pointer text-sm">
                  Live Performance
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/about" data-testid="nav-about-link" className="cursor-pointer text-sm">
                  About Us
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/#pricing" data-testid="nav-training-link" className="cursor-pointer text-sm">
                  Training
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/faq" data-testid="nav-faq-link" className="cursor-pointer text-sm">
                  FAQs
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/contact" data-testid="nav-contact-link" className="cursor-pointer text-sm">
                  Contact Us
                </a>
              </DropdownMenuItem>
              {/* Mobile-only auth entry */}
              <div className="sm:hidden border-t border-white/10 mt-1 pt-1">
                <DropdownMenuItem
                  data-testid="nav-signin-mobile"
                  onSelect={() => openAuth("login")}
                  className="cursor-pointer text-sm text-emerald-300"
                >
                  Sign in
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="hidden sm:block">
            <FounderChip />
          </div>
          <Button data-testid="landing-signin-btn" onClick={() => openAuth("login")}
            className="hidden sm:inline-flex rounded-full bg-white/10 text-white hover:bg-white/20 font-medium border border-white/20">
            Sign in
          </Button>
          <Button data-testid={TID.loginGoogleBtn} onClick={() => openAuth("register")}
            className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm shadow-[0_0_20px_-4px_rgba(250,204,21,0.6)]">
            Create Account
          </Button>
        </div>
      </header>

      <ShareBar />

      {/* Hero */}
      <section data-testid={TID.landingHero} className="relative z-10 px-4 sm:px-6 lg:px-12 pt-10 sm:pt-16 lg:pt-24 pb-16 sm:pb-20 max-w-7xl mx-auto">
        {/* TEAM WORK backdrop — far right, dimmer on mobile so text stays readable */}
        <img
          src="https://customer-assets-4nw71qhi.emergentagent.net/job_smart-buy-sell-20/artifacts/c38tgvke_TEAM%20WORK.png"
          alt=""
          aria-hidden
          data-testid="landing-teamwork-backdrop"
          className="pointer-events-none select-none absolute right-0 top-0 h-full w-auto max-w-[80%] sm:max-w-[65%] md:max-w-[55%] object-contain object-right opacity-30 sm:opacity-50 md:opacity-70 [mask-image:linear-gradient(to_left,black_55%,transparent_100%)]"
        />
        <div className="relative z-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.25em] text-emerald-300">
              <span className="pulse-dot" /> Live · Claude Sonnet 5 · Alpha Vantage
            </div>
            <div className="mt-3">
              <LiveDailyChip />
            </div>
            <blockquote
              data-testid="founder-quote"
              className="mt-6 max-w-xl border-l-2 border-emerald-400/40 pl-4 py-1"
            >
              <p className="text-[13px] sm:text-sm text-slate-300 italic leading-relaxed">
                &ldquo;You can work harder, earn more, and save more — but true financial growth begins
                when you learn to make your money grow, little by little, every day.&rdquo;
              </p>
              <footer className="mt-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-300 not-italic">
                — Charles Cawv Thao, Founder / Developer
              </footer>
            </blockquote>
            <h1 className="font-display mt-4 text-xl leading-snug sm:text-2xl lg:text-3xl font-extrabold sm:leading-snug tracking-tight break-words">
              Stock trading <span className="text-emerald-400">made simple.</span> <br/>
              Buy every dip. <span className="text-emerald-400">Sell every rip.</span> <br/>
              Repeat, <span className="text-emerald-400">automatically.</span>
            </h1>
            <p className="mt-5 sm:mt-6 max-w-xl text-slate-400 text-sm sm:text-base lg:text-lg leading-relaxed">
              TradingoX is a mean-reversion scalping bot: it hunts micro-dips to buy and micro-rips to sell, banking tiny profits over and over — hands-off, Claude Sonnet 5 in the loop, Alpaca on the exit ramp.
            </p>
            <p className="mt-3 max-w-xl text-emerald-300/90 text-xs sm:text-sm font-mono tracking-[0.02em] leading-relaxed">
              Precision Mode defaults: <b className="text-emerald-300">+0.55% take-profit</b> · <b className="text-emerald-300">–0.40% stop-loss</b> · <b className="text-emerald-300">70% confidence floor</b> · <b className="text-emerald-300">30% max drawdown auto-pause</b> — small wins, small losses.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button data-testid={TID.loginGoogleBtnHero} onClick={loginWithGoogle}
                className="rounded-full h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                Launch Terminal <ArrowUpRight className="ml-1 w-4 h-4" />
              </Button>
              <Button data-testid="landing-hero-create-account-btn" onClick={() => openAuth("register")}
                className="rounded-full h-12 px-6 bg-yellow-400 hover:bg-yellow-300 text-black font-semibold shadow-[0_0_24px_-4px_rgba(250,204,21,0.55)]">
                Create Account <ArrowUpRight className="ml-1 w-4 h-4" />
              </Button>
              <a href="#features" className="rounded-full h-12 px-6 inline-flex items-center border border-white/10 hover:border-white/25 text-slate-200 text-sm">
                Explore the engine
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg" data-testid="landing-hero-stats">
              {[
                { k: "Target win %", v: "65-70%", tone: "text-emerald-300" },
                { k: "Reward : Risk", v: "1.4 : 1" },
                { k: "Platform fee", v: "1.25%" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.k}</div>
                  <div className={`font-display text-2xl font-bold mt-1 ${s.tone || ""}`}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 lg:invisible" aria-hidden />
        </div>
      </section>

      {/* Live AI Signal Showcase — moved below hero so the team backdrop stays visible */}
      <section data-testid="landing-ai-signal-showcase" className="relative z-10 px-4 sm:px-6 lg:px-12 -mt-4 sm:-mt-8 pb-12 sm:pb-16 max-w-4xl mx-auto">
        <div className="aq-card-glass p-5 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="pulse-dot" />
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Live AI signal · NVDA</div>
            </div>
            <div className="font-mono text-[10px] text-slate-500">15:42:07 UTC</div>
          </div>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <div className="font-display text-4xl font-extrabold text-emerald-400">BUY</div>
              <div className="mt-1 text-slate-400 text-sm">Confidence <span className="text-emerald-300 font-mono">96.4%</span></div>
            </div>
            <TrendingUp className="w-14 h-14 text-emerald-400/70" />
          </div>
          <div className="mt-5 space-y-1 text-sm text-slate-300">
            <div className="flex justify-between"><span className="text-slate-500">Price</span><span className="font-mono">$871.42</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Target</span><span className="font-mono text-emerald-300">$914.98</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Stop</span><span className="font-mono text-rose-300">$845.28</span></div>
          </div>
          <div className="mt-5 border-t border-white/5 pt-4 text-sm text-slate-400 leading-relaxed">
            Short SMA(5) crossed above SMA(20) with rising momentum; sector rotation into semis confirms upside continuation.
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="aq-card p-4">
            <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.2em]"><TrendingUp className="w-3 h-3"/> Trend</div>
            <div className="mt-2 font-display text-xl font-bold">Bull cross</div>
          </div>
          <div className="aq-card p-4">
            <div className="flex items-center gap-2 text-rose-300 font-mono text-[10px] uppercase tracking-[0.2em]"><TrendingDown className="w-3 h-3"/> Risk</div>
            <div className="mt-2 font-display text-xl font-bold">3.0%</div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 px-4 sm:px-6 lg:px-12 py-12 sm:py-16 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8 sm:mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">The engine</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold mt-2">Built for the hands-off trader.</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <Bot className="w-5 h-5 text-emerald-300"/>, title: "Autonomous execution", body: "Set risk, position size & confidence floor. AURA does the rest, 24/5." },
            { icon: <LineChart className="w-5 h-5 text-indigo-300"/>, title: "Claude Sonnet 5 signals", body: "Every trade backed by ranked reasoning, catalysts and stop/target levels." },
            { icon: <ShieldCheck className="w-5 h-5 text-amber-300"/>, title: "Paper-first safety", body: "$100,000 simulated capital. Prove the strategy before you go live." },
            { icon: <Zap className="w-5 h-5 text-rose-300"/>, title: "Instant scan", body: "Fire an on-demand scan across your watchlist and see actionable trades in seconds." },
          ].map((f) => (
            <div key={f.title} className="aq-card p-5 hover:bg-[#1A1D2A] transition">
              <div className="w-10 h-10 rounded-lg bg-white/5 grid place-items-center">{f.icon}</div>
              <div className="mt-4 font-display text-lg font-bold">{f.title}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How */}
      <section id="how" className="relative z-10 px-4 sm:px-6 lg:px-12 py-12 sm:py-16 max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {[
            { n: "01", t: "Sign in", b: "Google login gets you a personal terminal in seconds." },
            { n: "02", t: "Set your rules", b: "Risk 1-10, position size, stop-loss, take-profit, confidence floor." },
            { n: "03", t: "Turn the bot on", b: "Watchlist scans run on demand; qualifying signals are auto-executed." },
          ].map((s) => (
            <div key={s.n} className="aq-card p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">{s.n}</div>
              <div className="mt-3 font-display text-2xl font-bold">{s.t}</div>
              <p className="mt-3 text-slate-400 text-sm leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
        <div className="mt-16 aq-card-glass p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Ready?</div>
            <div className="mt-2 font-display text-2xl lg:text-3xl font-bold">Trade smarter, on autopilot.</div>
            <div className="mt-2 text-slate-400 text-sm">Paper trading first. Broker holds all funds. Never trade money you cannot afford to lose.</div>
          </div>
          <Button onClick={loginWithGoogle} className="rounded-full h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
            Launch Terminal <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
        </div>

        <div className="mt-10 aq-card p-6 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-2 text-amber-200 font-display text-lg font-bold">
            <AlertTriangle className="w-5 h-5" /> Important risk & fund-custody notice
          </div>
          <p className="mt-3 text-sm text-slate-300 leading-relaxed">
            <b>Trading is highly risky.</b> Only trade money you can afford to lose. Do not borrow money to trade. Do not use your life savings.
            TradingoX <b>never holds or moves your funds</b> — your regulated broker (e.g., Alpaca) is the sole custodian. You control how much
            buying power your broker releases to us via API keys, and all deposits, withdrawals, and profit payouts are handled entirely by the broker.
          </p>
          <button onClick={() => setShowRisk(true)} className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-amber-200 hover:text-white underline underline-offset-4">
            Read full disclaimer →
          </button>
        </div>
      </section>

      <Pricing onCta={loginWithGoogle} />
      <LandingLeaderboard />

      {/* Founder desk background band (visible near footer) */}
      <section
        data-testid="landing-founder-band"
        className="relative z-10 mt-8 sm:mt-12"
      >
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="relative w-full overflow-hidden rounded-2xl bg-[#070810]">
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_smart-buy-sell-20/artifacts/se46vb2z_ChatGPT%20Image%20Sep%204%2C%202026%2C%2007_10_30%20PM.png"
              alt="Charles Cawv Thao, Founder — at the TradingoX desk"
              className="block w-full h-auto brightness-[0.6]"
            />
            {/* Soft bottom fade so the caption sits legibly on any monitor */}
            <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#070810] via-[#070810]/60 to-transparent" />

            <div className="absolute inset-x-0 bottom-0 z-10 p-4 sm:p-8">
              <div className="max-w-2xl backdrop-blur-sm bg-black/40 border border-white/10 rounded-2xl p-4 sm:p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">
                  Founder · Charles Cawv Thao
                </div>
                <blockquote className="mt-2 font-display text-base sm:text-xl lg:text-2xl font-bold leading-tight tracking-tight text-white">
                  &ldquo;Discipline beats emotion. Follow a system — not your feelings.&rdquo;
                </blockquote>
                <p className="mt-2 text-xs sm:text-sm text-slate-300 leading-relaxed">
                  Ten years in the making. Built on hard lessons, tested through real losses, delivered as a rules-based bot that trades so you don&rsquo;t have to sit in front of a screen.
                </p>
                <a
                  href="/about"
                  data-testid="landing-founder-band-cta"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs sm:text-sm font-semibold px-4 py-2"
                >
                  Read the story <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RiskFooter />
      <PromoVideoPopup />
      <Chatbot user={null} />
    </div>
  );
}
